import React, { useMemo, useState, useEffect } from "react";
import useSWR from "swr";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fetchBenchmarks, analyzeItem, analyzeAll, updateItemEdit } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { Search, Sparkles, Loader2, Trophy, Lock, Info } from "lucide-react";
import { toast } from "sonner";

// Compact rate-per-line comparison table:
// Original | AI Aggressive (low) | AI Market (typical) | My Rate (editable)
// Amounts column groups toggle-able. No High tier per user request.

export default function RateCompareTab({ data, onChange }) {
  const [q, setQ] = useState("");
  const [showAmounts, setShowAmounts] = useState(true);
  const [onlyIncluded, setOnlyIncluded] = useState(true);
  const [bulkRunning, setBulkRunning] = useState(false);

  const scenario = data.scenario;
  const isBaseline = scenario.is_baseline;

  const { data: benchmarks, mutate } = useSWR("benchmarks", fetchBenchmarks, { refreshInterval: bulkRunning ? 4000 : 0 });
  const bench = useMemo(() => benchmarks || {}, [benchmarks]);

  const rows = useMemo(() => {
    let list = data.rows;
    if (onlyIncluded) list = list.filter(r => r.included);
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(r => (r.description + " " + r.item_no_display + " " + r.sub_category).toLowerCase().includes(s));
    }
    return list;
  }, [data.rows, q, onlyIncluded]);

  const totals = useMemo(() => {
    let orig = 0, aggr = 0, mkt = 0, mine = 0;
    let orig_missing = 0, aggr_missing = 0, mkt_missing = 0;
    for (const r of rows) {
      const b = bench[r.id];
      orig += r.qty * r.rate;
      mine += r.current_qty * r.current_rate;
      if (b?.low_rate > 0) aggr += r.current_qty * b.low_rate; else { aggr_missing++; aggr += r.current_qty * r.current_rate; }
      if (b?.typical_rate > 0) mkt += r.current_qty * b.typical_rate; else { mkt_missing++; mkt += r.current_qty * r.current_rate; }
    }
    return { orig, aggr, mkt, mine, orig_missing, aggr_missing, mkt_missing };
  }, [rows, bench]);

  const analyzedCount = rows.filter(r => bench[r.id]).length;
  const missingCount = rows.length - analyzedCount;

  const runBulk = async () => {
    setBulkRunning(true);
    toast.info(`Analyzing ${missingCount} items — market rates`, { duration: 5000 });
    try {
      const res = await analyzeAll(false);
      toast.success(`Analyzed ${res.analyzed}${res.failed ? ` (${res.failed} failed)` : ""}`);
      mutate();
    } catch { toast.error("Bulk analysis failed"); }
    finally { setBulkRunning(false); }
  };

  const runOne = async (itemId) => {
    try {
      await analyzeItem(itemId);
      mutate();
    } catch (e) { toast.error(e?.response?.data?.detail?.slice(0, 80) || "Analysis failed"); }
  };

  const patchRate = async (itemId, newRate) => {
    try {
      await updateItemEdit(scenario.id, itemId, { rate: newRate });
      onChange();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap rounded-xl border border-border bg-white/95 shadow-sm p-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="ratecompare-search" />
        </div>

        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => setOnlyIncluded(v => !v)}
            className={`px-3 py-1.5 rounded-md border font-mono uppercase tracking-wider text-[11px] transition ${onlyIncluded ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"}`}
            data-testid="ratecompare-only-included"
          >
            {onlyIncluded ? "Included only" : "All items"}
          </button>
          <button
            onClick={() => setShowAmounts(v => !v)}
            className={`px-3 py-1.5 rounded-md border font-mono uppercase tracking-wider text-[11px] transition ${showAmounts ? "border-primary/40 bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/30"}`}
            data-testid="ratecompare-toggle-amounts"
          >
            {showAmounts ? "Rate + Amount" : "Rate only"}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          <Badge variant="outline" className="font-mono text-[10px]">
            {analyzedCount} / {rows.length} analyzed
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={runBulk}
            disabled={bulkRunning || missingCount === 0}
            className="border-primary/40 text-primary hover:bg-primary/5"
            data-testid="ratecompare-analyze-missing"
          >
            {bulkRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
            {bulkRunning ? "Analyzing…" : missingCount === 0 ? "All analyzed" : `Analyze ${missingCount} missing`}
          </Button>
        </div>
      </div>

      {isBaseline && (
        <div className="rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary flex items-center gap-2">
          <Lock className="h-3.5 w-3.5" />
          Reference baseline · exclude/edit items here to make them count as "original" in every comparison and vendor report.
        </div>
      )}

      {/* Grand totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <TotalCard label="Original" value={totals.orig} testId="total-original" />
        <TotalCard label="Market Aggressive" value={totals.aggr} tone="emerald" testId="total-aggressive"
          hint={totals.aggr_missing ? `${totals.aggr_missing} item(s) not yet analyzed used current rate` : null} />
        <TotalCard label="Market Typical" value={totals.mkt} tone="primary" testId="total-market"
          hint={totals.mkt_missing ? `${totals.mkt_missing} item(s) not yet analyzed used current rate` : null} />
        <TotalCard label="My Rate" value={totals.mine} tone="foreground" testId="total-my-rate" primary />
      </div>

      {/* Comparison table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card z-10 shadow-[0_1px_0_hsl(var(--border))]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 w-16">#</th>
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2 w-16">Unit</th>
                <th className="px-3 py-2 w-20 text-right">Qty</th>
                <th className="px-3 py-2 text-right border-l border-border/60">
                  <div className="font-mono">Original</div>
                  <div className="normal-case text-[9px] opacity-70">as-quoted</div>
                </th>
                <th className="px-3 py-2 text-right bg-emerald-50/40">
                  <div className="font-mono text-emerald-700">Market Aggressive</div>
                  <div className="normal-case text-[9px] opacity-70">negotiation floor</div>
                </th>
                <th className="px-3 py-2 text-right bg-primary/5">
                  <div className="font-mono text-primary">Market Typical</div>
                  <div className="normal-case text-[9px] opacity-70">realistic target</div>
                </th>
                <th className="px-3 py-2 text-right border-l border-border/60">
                  <div className="font-mono">My Rate</div>
                  <div className="normal-case text-[9px] opacity-70">click to edit</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => {
                const b = bench[r.id];
                const orig = r.rate;
                const aggr = b?.low_rate || 0;
                const mkt = b?.typical_rate || 0;
                const mine = r.current_rate;
                // Best-of pick among the 3 known columns (skip aggressive/market if missing)
                const candidates = [
                  { key: "orig", v: orig },
                  aggr > 0 ? { key: "aggr", v: aggr } : null,
                  mkt > 0 ? { key: "mkt", v: mkt } : null,
                  { key: "mine", v: mine },
                ].filter(Boolean);
                const cheapest = candidates.reduce((a, c) => c.v < a.v ? c : a, candidates[0]);
                return (
                  <RateCompareRow
                    key={r.id}
                    row={r}
                    idx={idx}
                    orig={orig}
                    aggr={aggr}
                    mkt={mkt}
                    mine={mine}
                    cheapest={cheapest.key}
                    showAmounts={showAmounts}
                    disabled={isBaseline}
                    benchmark={b}
                    onPatchRate={patchRate}
                    onAnalyze={runOne}
                  />
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground text-sm">No items match your search.</td></tr>
              )}
            </tbody>
            <tfoot className="bg-muted/40 border-t-2 border-border">
              <tr>
                <td colSpan={4} className="px-3 py-3 text-xs font-mono uppercase tracking-wider">
                  Totals ({rows.length} lines) — pick cheapest column
                </td>
                <td className="px-3 py-3 text-right num font-bold border-l border-border/60">{fmtMoney(totals.orig, false)}</td>
                <td className="px-3 py-3 text-right num font-bold bg-emerald-50/40 text-emerald-700">{fmtMoney(totals.aggr, false)}</td>
                <td className="px-3 py-3 text-right num font-bold bg-primary/5 text-primary">{fmtMoney(totals.mkt, false)}</td>
                <td className="px-3 py-3 text-right num font-bold border-l border-border/60">{fmtMoney(totals.mine, false)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
        <Trophy className="h-3 w-3 text-emerald-600" />
        <span>Cheapest rate per row is highlighted with a trophy. Market totals fall back to your current rate for any un-analyzed item.</span>
      </div>
    </div>
  );
}

function TotalCard({ label, value, tone = "muted", testId, hint, primary }) {
  const toneClass = {
    emerald: "text-emerald-700 border-emerald-500/40 bg-emerald-50/40",
    primary: "text-primary border-primary/40 bg-primary/5",
    foreground: "text-foreground border-primary/60 bg-primary/10",
    muted: "text-foreground border-border bg-card",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 ${toneClass} ${primary ? "ring-2 ring-primary/40" : ""}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-wider opacity-70">{label}</div>
        {hint && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="opacity-60 hover:opacity-100"><Info className="h-3 w-3" /></button>
            </PopoverTrigger>
            <PopoverContent className="w-64 text-xs">{hint}</PopoverContent>
          </Popover>
        )}
      </div>
      <div className="mt-1 num text-lg sm:text-xl font-bold">{fmtMoney(value)}</div>
    </div>
  );
}

function RateCompareRow({ row, idx, orig, aggr, mkt, mine, cheapest, showAmounts, disabled, benchmark, onPatchRate, onAnalyze }) {
  const [rate, setRate] = useState(mine);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => { setRate(mine); }, [mine]);

  const commit = () => {
    if (rate !== mine && !isNaN(rate)) onPatchRate(row.id, rate);
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try { await onAnalyze(row.id); } finally { setAnalyzing(false); }
  };

  const CellRate = ({ value, subValue, tone, isBest }) => (
    <div className={`text-right ${isBest ? "" : ""}`}>
      <div className={`num font-semibold ${tone || ""} ${isBest ? "flex items-center justify-end gap-1" : ""}`}>
        {isBest && <Trophy className="h-3 w-3 text-emerald-600" />}
        {value > 0 ? fmtMoney(value, false) : <span className="text-muted-foreground text-xs">—</span>}
      </div>
      {showAmounts && value > 0 && (
        <div className={`text-[10px] opacity-70 ${tone || "text-muted-foreground"}`}>{fmtMoney(value * row.current_qty, false)}</div>
      )}
    </div>
  );

  const desc = (row.description || "").split("\n")[0];

  return (
    <tr className={`border-b border-border/40 hover:bg-muted/30 ${idx % 2 === 1 ? "bg-muted/10" : ""} ${!row.included ? "opacity-50" : ""}`}>
      <td className="px-3 py-2 num text-xs text-muted-foreground align-top pt-3">{row.item_no_display}</td>
      <td className="px-3 py-2 align-top">
        <div className="text-sm leading-snug line-clamp-2 max-w-[520px]">{desc}</div>
        <div className="text-[10px] text-muted-foreground mt-0.5 font-mono uppercase tracking-wider">{row.section_title}</div>
      </td>
      <td className="px-3 py-2 text-xs text-muted-foreground align-top pt-3">{row.unit}</td>
      <td className="px-3 py-2 text-right num text-xs text-muted-foreground align-top pt-3">{row.current_qty}</td>

      <td className="px-3 py-2 border-l border-border/60 align-top pt-3">
        <CellRate value={orig} tone="text-foreground" isBest={cheapest === "orig"} />
      </td>

      <td className="px-3 py-2 bg-emerald-50/40 align-top pt-3">
        {aggr > 0 ? (
          <CellRate value={aggr} tone="text-emerald-700" isBest={cheapest === "aggr"} />
        ) : (
          <div className="text-right">
            {analyzing ? (
              <Loader2 className="h-3.5 w-3.5 inline animate-spin text-primary" />
            ) : (
              <Button variant="ghost" size="sm" className="h-6 px-1 text-[10px] text-primary" onClick={handleAnalyze} data-testid={`ratecompare-analyze-${row.id}`}>
                <Sparkles className="h-3 w-3 mr-0.5" /> Analyze
              </Button>
            )}
          </div>
        )}
      </td>

      <td className="px-3 py-2 bg-primary/5 align-top pt-3">
        {mkt > 0 ? (
          <CellRate value={mkt} tone="text-primary" isBest={cheapest === "mkt"} />
        ) : (
          <div className="text-right text-muted-foreground text-xs">—</div>
        )}
      </td>

      <td className="px-3 py-2 border-l border-border/60 align-top">
        <div className="flex items-center justify-end gap-1">
          {cheapest === "mine" && <Trophy className="h-3 w-3 text-emerald-600 shrink-0" />}
          <Input
            type="number"
            step="0.01"
            value={rate}
            disabled={disabled}
            onChange={e => setRate(parseFloat(e.target.value) || 0)}
            onBlur={commit}
            className="h-7 w-24 text-right num text-sm"
            data-testid={`ratecompare-myrate-${row.id}`}
          />
        </div>
        {showAmounts && (
          <div className="text-[10px] text-muted-foreground mt-0.5 text-right">{fmtMoney(rate * row.current_qty, false)}</div>
        )}
      </td>
    </tr>
  );
}
